import React from "react";
// UI Framework
import { TableCell } from "@mui/material";
import TimelineIcon from "@mui/icons-material/Timeline";
// Common Components
import Button from "src/Components/Button";
import DatePicker from "src/Components/DatePicker";
import FullScreenLoading from "src/Components/Loading/FullScreenLoading";
import Modal from "src/Components/Modal/Modal";
import { WrappedTimeSeriesChart } from "src/Components/Timeseries/WrappedTimeSeriesChart";
// Requests
import { fundCompositionGraph } from "src/Requests/goldArbitrage";
// Utilities
import { packetErrorHandler } from "src/Utils/requestUtils";
// Types
import { IAdditionalHeaderProps } from "src/Tables/TableTemplate/tableTemplate";
import { Graph_Dto } from "src/Interfaces/orderbookGraphInterfaces";
import { IFundCompositionTableReport } from "src/Interfaces/goldArbitrage";

export const CompositionChartHeader = ({}: IAdditionalHeaderProps) => {
  return <TableCell />;
};

function getDateFromToday(monthCount: number) {
  const targetDate = new Date();
  targetDate.setMonth(targetDate.getMonth() - monthCount);
  return targetDate;
}

export const CompositionChartCol: React.FC<{
  row: IFundCompositionTableReport;
  onAction: () => void;
}> = React.memo(({ row }) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [graphData, setGraphData] = React.useState<Graph_Dto[]>([]);
  const [fromDate, setFromDate] = React.useState(
    getDateFromToday(2).toISOString(),
  );
  const [toDate, setToDate] = React.useState(new Date().toISOString());

  const formatDate = (date: string) => date?.split("T")[0] ?? date;

  const onSubmit = () => {
    setLoading(true);
    fundCompositionGraph({
      instrumentId: row.instrumentId ?? "",
      fromDate: formatDate(fromDate),
      toDate: formatDate(toDate),
    })
      .then((resp: Graph_Dto | Graph_Dto[]) => {
        setGraphData(Array.isArray(resp) ? resp : resp ? [resp] : []);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  React.useEffect(() => {
    if (!isModalOpen) return;
    onSubmit();
  }, [isModalOpen]);

  const handleFromDate = (_: string, value: string) => setFromDate(value);
  const handleToDate = (_: string, value: string) => setToDate(value);

  const setDateFromBtns = (monthCount: number) => {
    const today = new Date();
    setFromDate(getDateFromToday(monthCount).toISOString());
    setToDate(today.toISOString());
  };

  const getDateSetterBtn = (monthCount: number) => (
    <Button
      text={`${monthCount} ماه`}
      onClick={() => setDateFromBtns(monthCount)}
      variant={
        fromDate?.split("T")[0] ===
        getDateFromToday(monthCount).toISOString()?.split("T")[0]
          ? "contained"
          : "outlined"
      }
    />
  );

  const ModalUI = () => (
    <div className="w-full flex flex-col justify-start items-start gap-3 px-16">
      {loading ? <FullScreenLoading /> : <></>}
      <span className="font-bold">ترکیب دارایی {row.name ?? ""}</span>
      <div className="w-full flex flex-row justify-start items-start gap-5">
        <div className="flex shrink-0 flex-col justify-start items-start gap-3">
          <DatePicker
            label="از تاریخ"
            name="fromDate"
            onChange={handleFromDate}
            value={fromDate}
          />
          <DatePicker
            label="تا تاریخ"
            name="toDate"
            onChange={handleToDate}
            value={toDate}
          />
          <div className="flex flex-row justify-start items-center gap-1 w-full">
            {getDateSetterBtn(1)}
            {getDateSetterBtn(3)}
            {getDateSetterBtn(6)}
          </div>
          <Button text="جستجو" onClick={onSubmit} variant="contained" />
        </div>
        <div className="chart-fill-modal font-bold">
          <WrappedTimeSeriesChart
            dataset={graphData}
            withDate
            YValueType="priceWithDecimal"
          className="h-full w-full" />
        </div>
      </div>
    </div>
  );

  return (
    <TableCell>
      {isModalOpen ? (
        <Modal
          UI={ModalUI()}
          title={`ترکیب دارایی ${row.name ?? ""}`.trim()}
          onClose={() => setIsModalOpen(false)}
          fullScreen
        />
      ) : (
        <></>
      )}
      <div className="w-20 py-1 px-2">
        <Button
          text={
            <>
              <TimelineIcon style={{ fontSize: 16, marginLeft: 4 }} /> نمودار
            </>
          }
          onClick={() => setIsModalOpen(true)}
          variant="contained"
          className="table-btn "
        />
      </div>
    </TableCell>
  );
});
